import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * CargoLinesEditor — rows of package details for a shipment.
 * Each row: package_type / length_cm / width_cm / height_cm / weight_kg.
 * Auto-computed per row: CBM (L*W*H / 1_000_000) and, if AIR, chargeable_weight (L*W*H / 5000).
 * The parent owns the array and totals; this component just fires onChange with the mutated list.
 */
const emptyLine = () => ({
  package_type: "",
  length_cm: "",
  width_cm: "",
  height_cm: "",
  weight_kg: "",
});

function lineCbm(l) {
  const L = Number(l.length_cm), W = Number(l.width_cm), H = Number(l.height_cm);
  if (!L || !W || !H) return 0;
  return (L * W * H) / 1_000_000;
}

function lineChargeable(l) {
  const L = Number(l.length_cm), W = Number(l.width_cm), H = Number(l.height_cm);
  if (!L || !W || !H) return 0;
  return (L * W * H) / 5000;
}

export function computeCargoTotals(lines, isAir) {
  const arr = lines || [];
  const totalWeight = arr.reduce((s, l) => s + (Number(l.weight_kg) || 0), 0);
  const totalCbm = arr.reduce((s, l) => s + lineCbm(l), 0);
  const totalChargeable = arr.reduce((s, l) => s + (isAir ? Math.max(lineChargeable(l), Number(l.weight_kg) || 0) : 0), 0);
  return {
    total_weight: Number(totalWeight.toFixed(3)),
    total_cbm: Number(totalCbm.toFixed(3)),
    total_chargeable_weight: Number(totalChargeable.toFixed(3)),
  };
}

export default function CargoLinesEditor({ value = [], onChange, isAir }) {
  const lines = value.length ? value : [];

  const update = (idx, key, v) => {
    const next = lines.slice();
    next[idx] = { ...(next[idx] || emptyLine()), [key]: v };
    onChange(next);
  };
  const add = () => onChange([...lines, emptyLine()]);
  const remove = (idx) => onChange(lines.filter((_, i) => i !== idx));

  return (
    <div className="w-full">
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500 mb-2">
        Cargo Details (per package)
      </div>

      {lines.length === 0 && (
        <div className="text-[11px] text-zinc-500 font-mono mb-2 p-3 bg-zinc-50 border border-dashed border-zinc-300 rounded">
          No cargo lines added — you can enter the totals manually below, or click <strong>+ Add line</strong> to break down per package.
        </div>
      )}

      {lines.length > 0 && (
        <div className="overflow-x-auto border border-zinc-200 rounded-md mb-2">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-[0.12em] text-zinc-500">
              <tr>
                <th className="px-2 py-2 text-left">Type</th>
                <th className="px-2 py-2 text-right">L (cm)</th>
                <th className="px-2 py-2 text-right">W (cm)</th>
                <th className="px-2 py-2 text-right">H (cm)</th>
                <th className="px-2 py-2 text-right">Wt (kg)</th>
                <th className="px-2 py-2 text-right">CBM</th>
                {isAir && <th className="px-2 py-2 text-right">Chg. Wt (kg)</th>}
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {lines.map((l, idx) => (
                <tr key={`line-${idx}`} className="border-t border-zinc-100" data-testid={`cargo-line-${idx}`}>
                  <td className="px-1 py-1">
                    <Input value={l.package_type || ""} onChange={(e) => update(idx, "package_type", e.target.value)} placeholder="Carton" className="h-8 text-xs" data-testid={`cargo-type-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <Input type="number" step="0.1" value={l.length_cm} onChange={(e) => update(idx, "length_cm", e.target.value)} className="h-8 text-xs text-right" data-testid={`cargo-length-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <Input type="number" step="0.1" value={l.width_cm} onChange={(e) => update(idx, "width_cm", e.target.value)} className="h-8 text-xs text-right" data-testid={`cargo-width-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <Input type="number" step="0.1" value={l.height_cm} onChange={(e) => update(idx, "height_cm", e.target.value)} className="h-8 text-xs text-right" data-testid={`cargo-height-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <Input type="number" step="0.01" value={l.weight_kg} onChange={(e) => update(idx, "weight_kg", e.target.value)} className="h-8 text-xs text-right" data-testid={`cargo-weight-${idx}`} />
                  </td>
                  <td className="px-2 py-1 text-right text-zinc-700" data-testid={`cargo-cbm-${idx}`}>
                    {lineCbm(l).toFixed(3)}
                  </td>
                  {isAir && (
                    <td className="px-2 py-1 text-right text-zinc-700" data-testid={`cargo-cw-${idx}`}>
                      {lineChargeable(l).toFixed(3)}
                    </td>
                  )}
                  <td className="px-1 py-1 text-center">
                    <button type="button" onClick={() => remove(idx)} className="text-zinc-400 hover:text-red-600 p-1" data-testid={`cargo-remove-${idx}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Button type="button" variant="outline" size="sm" onClick={add} className="text-xs" data-testid="cargo-add-line">
        <Plus className="w-3.5 h-3.5 mr-1" /> Add line
      </Button>
      <p className="text-[10px] text-zinc-500 font-mono mt-2">
        CBM = L × W × H / 1,000,000. {isAir ? "Chargeable weight = L × W × H / 5000. Total uses max(chg wt, actual wt) per line." : ""}
      </p>
    </div>
  );
}
